import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useT } from '../lib/i18n';

export default function Login() {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);   // default ON — standard mobile-app behavior
  const [busy, setBusy] = useState(false);
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();

  // If a valid session already exists, skip the login screen entirely.
  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [loading, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password, remember);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const quickFill = (role) => {
    setEmail(`${role}@test.com`);
    setPassword('test123');
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-block mb-8" data-testid="login-back-home">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-navy flex items-center justify-center text-gold font-heading text-xl font-bold">O</div>
            <span className="font-heading text-2xl font-bold text-navy">OuiStuPaStu</span>
          </div>
        </Link>

        <div className="bg-white rounded-3xl p-8 shadow-squishy border-2 border-cream">
          <h1 className="font-heading text-3xl font-bold text-navy mb-2">{t('auth.welcome_back')}</h1>
          <p className="text-navy/60 mb-6">{t('auth.login_desc')}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">{t('auth.email')}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-base"
                placeholder="you@family.com" data-testid="login-email-input" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">{t('auth.password')}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-base"
                placeholder="••••••••" data-testid="login-password-input" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                className="w-5 h-5 rounded accent-teal cursor-pointer" data-testid="remember-me-checkbox" />
              <span className="text-sm text-navy/80 font-semibold">Keep me signed in <span className="text-navy/50 font-normal">(for 30 days on this device)</span></span>
            </label>
            <button type="submit" disabled={busy}
              className="w-full h-14 rounded-full bg-navy text-white font-heading font-bold text-lg hover:scale-[1.02] active:scale-95 transition-transform duration-200 shadow-squishy disabled:opacity-60"
              data-testid="login-submit-button">
              {busy ? t('auth.signing_in') : t('auth.login_btn')}
            </button>
          </form>

          <div className="mt-6 border-t border-slate-100 pt-5">
            <p className="text-xs text-navy/50 mb-3 text-center">{t('auth.quick_demo')}</p>
            <div className="grid grid-cols-3 gap-2">
              {['parent', 'child', 'teacher'].map((r) => (
                <button key={r} type="button" onClick={() => quickFill(r)}
                  className="px-3 py-2 rounded-xl bg-cream text-navy font-semibold text-sm capitalize hover:bg-teal hover:text-white transition-colors duration-200"
                  data-testid={`demo-fill-${r}`}>{r}</button>
              ))}
            </div>
          </div>

          <p className="mt-6 text-center text-navy/70">
            {t('auth.new_here')}{' '}
            <Link to="/register" className="text-teal font-bold hover:underline" data-testid="login-to-register-link">
              {t('auth.create_account')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
